import { useEffect } from "react";
import * as THREE from "three";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useSceneStore } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { applyJointValue } from "../kinematics";
import { getScalarJointRelativeValue } from "../joints/scalarJointValue";
import { devLog } from "../scene/sceneConstants";

export function isJointSnapshotKinematicallyDisplaced(jointSnapshot) {
  const joint = jointSnapshot?.joint;
  const kind = String(joint?.kind || "").toLowerCase();
  const current = Number(jointSnapshot?.current ?? joint?.ref ?? 0);
  if (!Number.isFinite(current)) return false;
  if (kind === "hinge" || kind === "slide") {
    return Math.abs(getScalarJointRelativeValue(joint, current)) > 1e-6;
  }
  return Math.abs(current) > 1e-6;
}

export function useSceneBodyTransformSync({
  bodyTree,
  collectDescendantsByUuid,
  getJointSnapshot,
  isJointUpdatingRef,
  jointObjectsRef,
  safeBodies,
  sceneObjects,
}) {
  useEffect(() => {
    if (usePhysicsStore.getState().isRunning) return;
    if (isJointUpdatingRef.current) return;

    Object.values(safeBodies).forEach((body) => {
      if (!body || !body.uuid) return;

      const bodyUuid = body.uuid;
      const bodyName = body.name || bodyUuid;
      const sceneObj = sceneObjects[bodyUuid];
      if (!sceneObj) return;

      let positionChanged = false;
      let rotationChanged = false;

      const jointEntriesForBody = Object.keys(jointObjectsRef.current || {}).filter((jointName) => {
        const entry = jointObjectsRef.current[jointName];
        if (!entry || !entry.bodyGroup) return false;
        const bodyGroupUuid = entry.bodyGroup.userData?.bodyUuid;
        return bodyGroupUuid && bodyGroupUuid === bodyUuid;
      });
      let isKinematicallyControlled = false;
      try {
        isKinematicallyControlled = jointEntriesForBody.some((jointName) => {
          const jointSnapshot = getJointSnapshot(jointName) || { current: 0 };
          return isJointSnapshotKinematicallyDisplaced(jointSnapshot);
        });
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }

      const storedPosition = (() => {
        const state = useSceneStore.getState();
        return state.bodyPositions?.[bodyUuid];
      })();
      if (storedPosition && sceneObj.position) {
        const currentPos = [sceneObj.position.x, sceneObj.position.y, sceneObj.position.z];
        positionChanged = currentPos.some((val, i) => Math.abs(val - storedPosition[i]) > 0.001);

        if (positionChanged && !isKinematicallyControlled) {
          devLog(`[Scene] Syncing stored position for ${bodyName} (${bodyUuid}):`, storedPosition);
          sceneObj.position.set(storedPosition[0], storedPosition[1], storedPosition[2]);
        }
      }

      const storedRotation = (() => {
        const state = useSceneStore.getState();
        return state.bodyRotations?.[bodyUuid];
      })();
      if (storedRotation && sceneObj.quaternion) {
        const currentQuat = [
          sceneObj.quaternion.w,
          sceneObj.quaternion.x,
          sceneObj.quaternion.y,
          sceneObj.quaternion.z,
        ];
        rotationChanged = currentQuat.some((val, i) => Math.abs(val - storedRotation[i]) > 0.001);

        if (rotationChanged && !isKinematicallyControlled) {
          sceneObj.quaternion.set(
            storedRotation[1],
            storedRotation[2],
            storedRotation[3],
            storedRotation[0]
          );
        }
      }

      if ((positionChanged || rotationChanged) && !isKinematicallyControlled) {
        const uuidsToUpdate = [bodyUuid, ...collectDescendantsByUuid(bodyUuid)];
        uuidsToUpdate.forEach((descendantUuid) => {
          const obj = descendantUuid === bodyUuid ? sceneObj : sceneObjects[descendantUuid];
          if (!obj) return;

          const entryNames = Object.keys(jointObjectsRef.current || {}).filter((jointName) => {
            const entry = jointObjectsRef.current[jointName];
            if (!entry || !entry.bodyGroup) return false;
            const bodyGroupUuid = entry.bodyGroup.userData?.bodyUuid;
            return bodyGroupUuid && bodyGroupUuid === descendantUuid;
          });

          entryNames.forEach((jointName) => {
            const entry = jointObjectsRef.current[jointName];
            if (!entry) return;

            const state = useSceneStore.getState();
            const storePos = state.bodyPositions?.[descendantUuid];
            const storeRot = state.bodyRotations?.[descendantUuid];
            if (Array.isArray(storePos) && storePos.length === 3) {
              entry.initialPos = new THREE.Vector3(storePos[0], storePos[1], storePos[2]);
            } else {
              entry.initialPos = obj.position.clone();
            }
            if (Array.isArray(storeRot) && storeRot.length === 4) {
              entry.initialRot = new THREE.Quaternion(
                storeRot[1],
                storeRot[2],
                storeRot[3],
                storeRot[0]
              );
            } else {
              entry.initialRot = obj.quaternion.clone();
            }
            try {
              const parent = obj.parent;
              const parentRot = new THREE.Quaternion();
              if (parent && parent.getWorldQuaternion) parent.getWorldQuaternion(parentRot);
              entry.parentRot = parentRot;
            } catch (error) {
              logger.warn("[Scene] Unexpected error during scene update", error);
            }

            try {
              const jointSnapshot = getJointSnapshot(jointName) || { current: 0 };
              applyJointValue(
                entry.bodyGroup,
                entry.joint,
                jointSnapshot.current,
                entry.initialRot,
                entry.initialPos,
                entry.parentRot
              );
            } catch (error) {
              logger.warn(
                "[Scene] Failed to re-apply joint after baseline change",
                jointName,
                error
              );
            }
          });
        });
      }
    });
  }, [
    bodyTree,
    sceneObjects,
    collectDescendantsByUuid,
    getJointSnapshot,
    isJointUpdatingRef,
    jointObjectsRef,
    safeBodies,
  ]);
}
